// Course Context
// Manages course data and current course state throughout the app

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { getCourses, getCourseById, getModules, getLessons, getProgress } from '@features/courses';
import { useAuth } from '@features/auth';
import { Course } from '@features/courses/api/course-services';
import { Module } from '@features/courses/api/module-services';
import { Lesson } from '@features/courses/api/lesson-services';

interface CourseProgress {
  [courseId: string]: any;
}

interface CourseContextValue {
  courses: Course[];
  currentCourse: Course | null;
  currentModule: Module | null;
  currentLesson: Lesson | null;
  modules: Module[];
  lessons: Lesson[];
  loading: boolean;
  error: Error | null;
  courseProgress: CourseProgress;
  fetchCourses: () => Promise<Course[]>;
  fetchCourseById: (courseId: string) => Promise<Course>;
  fetchLessons: (courseId: string, moduleId: string) => Promise<Lesson[]>;
  selectModule: (moduleId: string) => Promise<void>;
  selectLesson: (lessonId: string) => void;
  getNextLesson: () => Lesson | null;
  getPreviousLesson: () => Lesson | null;
  fetchCourseProgress: (courseId: string) => Promise<any>;
  getEnrolledCourses: () => Course[];
  isEnrolledInCourse: (courseId: string) => boolean;
  getCourseCompletionPercentage: (courseId: string) => number;
  clearCurrentCourse: () => void;
}

const CourseContext = createContext<CourseContextValue | undefined>(undefined);

// Custom hook to use course context
export const useCourse = (): CourseContextValue => {
  const context = useContext(CourseContext);
  if (!context) {
    throw new Error('useCourse must be used within a CourseProvider');
  }
  return context;
};

interface CourseProviderProps {
  children: ReactNode;
}

export const CourseProvider: React.FC<CourseProviderProps> = ({ children }) => {
  const { user } = useAuth();

  // State management
  const [courses, setCourses] = useState<Course[]>([]);
  const [currentCourse, setCurrentCourse] = useState<Course | null>(null);
  const [currentModule, setCurrentModule] = useState<Module | null>(null);
  const [currentLesson, setCurrentLesson] = useState<Lesson | null>(null);
  const [modules, setModules] = useState<Module[]>([]);
  const [lessons, setLessons] = useState<Lesson[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const [courseProgress, setCourseProgress] = useState<CourseProgress>({});

  // Fetch all courses
  const fetchCourses = async (): Promise<Course[]> => {
    try {
      setLoading(true);
      setError(null);
      const coursesData = await getCourses();
      setCourses(coursesData);
      return coursesData;
    } catch (err: any) {
      setError(err);
      console.error('Error fetching courses:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Fetch course by ID
  const fetchCourseById = async (courseId: string): Promise<Course> => {
    try {
      setLoading(true);
      setError(null);
      const courseData = await getCourseById(courseId);
      setCurrentCourse(courseData);

      // Fetch modules for this course
      const modulesData = await getModules(courseId);
      setModules(modulesData);

      return courseData;
    } catch (err: any) {
      setError(err);
      console.error('Error fetching course:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Fetch lessons for a module
  const fetchLessons = async (courseId: string, moduleId: string): Promise<Lesson[]> => {
    try {
      setLoading(true);
      setError(null);
      const lessonsData = await getLessons(courseId, moduleId);
      setLessons(lessonsData);
      return lessonsData;
    } catch (err: any) {
      setError(err);
      console.error('Error fetching lessons:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Set current module
  const selectModule = async (moduleId: string): Promise<void> => {
    const module = modules.find(m => m.id === moduleId);
    if (module) {
      setCurrentModule(module);
      if (currentCourse) {
        await fetchLessons(currentCourse.id, moduleId);
      }
    }
  };

  // Set current lesson
  const selectLesson = (lessonId: string): void => {
    const lesson = lessons.find(l => l.id === lessonId);
    if (lesson) {
      setCurrentLesson(lesson);
    }
  };

  // Get next lesson
  const getNextLesson = (): Lesson | null => {
    if (!currentLesson || lessons.length === 0) return null;

    const currentIndex = lessons.findIndex(l => l.id === currentLesson.id);
    if (currentIndex < lessons.length - 1) {
      return lessons[currentIndex + 1];
    }
    return null;
  };

  // Get previous lesson
  const getPreviousLesson = (): Lesson | null => {
    if (!currentLesson || lessons.length === 0) return null;

    const currentIndex = lessons.findIndex(l => l.id === currentLesson.id);
    if (currentIndex > 0) {
      return lessons[currentIndex - 1];
    }
    return null;
  };

  // Fetch user's course progress
  const fetchCourseProgress = async (courseId: string): Promise<any> => {
    if (!user) return null;

    try {
      const progress = await getProgress(user.uid, courseId);
      setCourseProgress(prev => ({
        ...prev,
        [courseId]: progress
      }));
      return progress;
    } catch (err) {
      console.error('Error fetching course progress:', err);
      return null;
    }
  };

  // Get enrolled courses for current user
  const getEnrolledCourses = (): Course[] => {
    if (!user || !courseProgress) return [];

    return courses.filter(course =>
      courseProgress[course.id] && courseProgress[course.id].enrolled
    );
  };

  // Check if user is enrolled in a course
  const isEnrolledInCourse = (courseId: string): boolean => {
    if (!user || !courseProgress[courseId]) return false;
    return courseProgress[courseId].enrolled === true;
  };

  // Get course completion percentage
  const getCourseCompletionPercentage = (courseId: string): number => {
    if (!courseProgress[courseId]) return 0;

    const progress = courseProgress[courseId];
    if (!progress.totalLessons || progress.totalLessons === 0) return 0;

    return Math.round((progress.completedLessons / progress.totalLessons) * 100);
  };

  // Clear current course data
  const clearCurrentCourse = (): void => {
    setCurrentCourse(null);
    setCurrentModule(null);
    setCurrentLesson(null);
    setModules([]);
    setLessons([]);
  };

  // Load courses on mount
  useEffect(() => {
    fetchCourses().catch(err => {
      console.warn('Silent: Failed to load courses in provider on mount:', err.message || err);
    });
  }, []);

  // Load progress for all courses when user changes
  useEffect(() => {
    if (user && courses.length > 0) {
      courses.forEach(course => {
        fetchCourseProgress(course.id);
      });
    }
    // eslint_disable-next-line react-hooks/exhaustive-deps
  }, [user, courses]);

  const value: CourseContextValue = {
    // State
    courses,
    currentCourse,
    currentModule,
    currentLesson,
    modules,
    lessons,
    loading,
    error,
    courseProgress,

    // Methods
    fetchCourses,
    fetchCourseById,
    fetchLessons,
    selectModule,
    selectLesson,
    getNextLesson,
    getPreviousLesson,
    fetchCourseProgress,
    getEnrolledCourses,
    isEnrolledInCourse,
    getCourseCompletionPercentage,
    clearCurrentCourse
  };

  return (
    <CourseContext.Provider value={value}>
      {children}
    </CourseContext.Provider>
  );
};

// Export provider as default for HMR compatibility
export default CourseProvider;
